import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  createInvoice,
  getInvoiceData,
} from "../features/invoice/invoiceSlice";
import { getStates } from "../features/statecity/statecitySlice";
import { getUsers } from "../features/cutomers/customerSlice";

const styleSheetContent = `
  .invoice-card {
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    padding: 24px;
    border-radius: 16px;
    border: 1px solid #eef2f6;
    margin-bottom: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
  }
  .invoice-card:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
    border-color: #d1d9e6;
  }
  .form-input {
    width: 100%;
    height: 42px;
    padding: 10px 14px;
    border: 1px solid #cbd5e0;
    border-radius: 8px;
    font-size: 13px;
    color: #1a2332;
    background-color: #ffffff;
    box-sizing: border-box;
    transition: all 0.2s ease;
    outline: none;
  }
  .form-input:hover {
    border-color: #4a90d9;
  }
  .form-input:focus {
    border-color: #4a90d9;
    box-shadow: 0 0 0 3px rgba(74, 144, 217, 0.1);
  }
  .form-input:disabled {
    background-color: #f1f5f9;
    border-color: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
  }
  .form-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }
  .form-group {
    display: flex;
    flex-direction: column;
  }
  .submit-btn {
    background: linear-gradient(135deg, #4a90d9 0%, #357abd 100%);
    color: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.25s ease;
    box-shadow: 0 4px 12px rgba(74, 144, 217, 0.3);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .submit-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(74, 144, 217, 0.4);
    background: linear-gradient(135deg, #357abd 0%, #2a6198 100%);
  }
  .submit-btn:active {
    transform: translateY(0);
  }
`;

const UploadInvoiceAccordion = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const dispatch = useDispatch();

  const user = useSelector((state) => state.auth.user);
  const userName = user?.name || "Current User";
  const { statelist } = useSelector((state) => state.statecity);
  const { customers: vclist } = useSelector((state) => state.customer);

  const [formData, setFormData] = useState({
    invoice_date: "",
    state: "",
    vc_name: "",
    invoice_no: "",
    invoice_amount: "",
    invoiceFile: null,
    created_by: userName,
    courier_company: "",
    consignment_no: "",
    bill_period: "",
    bill_type: "regular",
    district: "",
    bill_month: "",
    bill_year: "",
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    let newValue = value;
    if (type === "checkbox") {
      newValue = e.target.checked;
    } else if (type === "number") {
      newValue = parseFloat(value) || 0;
    } else if (type === "file" && files && files.length > 0) {
      newValue = files[0];
    }

    if (name === "state") {
      setFormData((prev) => ({
        ...prev,
        state: value,
        district: "",
        vc_name: "",
      }));
    } else if (name === "district") {
      setFormData((prev) => ({
        ...prev,
        district: value,
        vc_name: "",
      }));
    } else if (name === "bill_month" || name === "bill_year") {
      setFormData((prev) => {
        const updatedData = { ...prev, [name]: newValue };
        // Combine month and year into bill_period
        if (updatedData.bill_month && updatedData.bill_year) {
          updatedData.bill_period = `${updatedData.bill_month} ${updatedData.bill_year}`;
        } else if (updatedData.bill_month) {
          updatedData.bill_period = updatedData.bill_month;
        } else if (updatedData.bill_year) {
          updatedData.bill_period = updatedData.bill_year;
        }
        return updatedData;
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: newValue }));
    }
  };

  useEffect(() => {
    dispatch(getStates());
    dispatch(getUsers());
  }, [dispatch]);

  const getDistrictsForState = () => {
    if (!formData.state || !statelist) return [];
    const selectedState = statelist.find(
      (state) => state.state_name === formData.state
    );
    return selectedState ? selectedState.district_name : [];
  };

  // Modified function to filter VCs based only on state
  const getVCsForStateAndDistrict = () => {
    if (!formData.state || !vclist) return [];

    const normalizedState =
      formData.state.match(/\(([^)]+)\)/)?.[1] || formData.state;

    const filteredVCs = vclist.filter(
      (vc) => vc.role === "vc" && vc.state === normalizedState
    );

    return filteredVCs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const invoicedata = new FormData();
    let formattedInvoiceDate = "";
    if (formData.invoice_date) {
      const date = new Date(formData.invoice_date);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      formattedInvoiceDate = `${day}/${month}/${year}`;
    }
    const finalFormData = {
      ...formData,
      invoice_date: formattedInvoiceDate,
      created_by: userName,
      district: formData.district || "N/A",
    };

    Object.entries(finalFormData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (typeof value === "boolean") {
          invoicedata.append(key, value.toString());
        } else if (key === "invoiceFile" && value instanceof File) {
          invoicedata.append(key, value);
        } else {
          invoicedata.append(key, value);
        }
      }
    });

    dispatch(createInvoice(invoicedata))
      .unwrap()
      .then((response) => {
        toast.success("Invoice uploaded successfully!", { autoClose: 3000 });
        dispatch(getInvoiceData({}))
          .unwrap()
          .then((result) => {})
          .catch((error) => {
            console.error("Failed to fetch updated invoices:", error);
            toast.error("Failed to refresh invoice data after upload.");
          });

        setFormData({
          invoice_date: "",
          state: "",
          vc_name: "",
          invoice_no: "",
          invoice_amount: "",
          invoiceFile: null,
          created_by: userName,
          courier_company: "",
          consignment_no: "",
          bill_period: "",
          bill_type: "regular",
          district: "",
          bill_month: "",
          bill_year: "",
        });
        setIsOpen(false);
      })
      .catch((error) => {
        toast.error(
          `Failed to upload invoice: ${
            error.response?.data?.msg || error.msg || "Please try again."
          }`,
          { autoClose: 3000 }
        );
        // notify parent to refresh chart after alert closed (keeps old data visible)
        setTimeout(
          () => window.dispatchEvent(new Event("refreshInvoiceChart")),
          120
        );
      });
  };

  return (
    <div className="invoice-card">
      <style>{styleSheetContent}</style>

      {/* Decorative Background Element */}
      <div style={{
        position: "absolute",
        top: "-50px",
        right: "-50px",
        width: "200px",
        height: "200px",
        borderRadius: "50%",
        background: "linear-gradient(135deg, rgba(74, 144, 217, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%)",
        pointerEvents: "none",
      }} />

      {/* Header Row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          position: "relative",
          zIndex: 1,
          flexWrap: isMobile ? "wrap" : "nowrap",
          gap: isMobile ? "12px" : "0",
        }}
        onClick={toggleAccordion}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{
            width: "48px",
            height: "48px",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #4a90d9 0%, #357abd 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "20px",
            color: "#fff",
            flexShrink: 0,
            boxShadow: "0 4px 12px rgba(74, 144, 217, 0.3)",
          }}>
            📝
          </div>
          <div>
            <h2 style={{ fontWeight: 700, fontSize: isMobile ? "18px" : "20px", margin: 0, color: "#1a2332", letterSpacing: "-0.02em" }}>
              Manual Invoice Entry
            </h2>
            <p style={{ margin: "2px 0 0 0", fontSize: "13px", color: "#64748b" }}>
              Create and upload a single invoice manually
            </p>
          </div>
        </div>

        {/* Styled Collapse Pill */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "8px 16px",
          borderRadius: "100px",
          background: isOpen ? "rgba(239, 68, 68, 0.08)" : "rgba(74, 144, 217, 0.08)",
          border: `1px solid ${isOpen ? "rgba(239, 68, 68, 0.15)" : "rgba(74, 144, 217, 0.15)"}`,
          color: isOpen ? "#ef4444" : "#4a90d9",
          fontWeight: 600,
          fontSize: "12px",
          transition: "all 0.2s ease",
        }}>
          {isOpen ? "✕ Collapse" : "＋ Add Invoice"}
        </div>
      </div>

      {isOpen && (
        <form
          onSubmit={handleSubmit}
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: "20px",
            marginTop: "24px",
            paddingTop: "20px",
            borderTop: "1px solid #f0f0f0",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Invoice Date */}
          <div className="form-group">
            <label className="form-label">Invoice Date <span style={{ color: "#ef4444" }}>*</span></label>
            <input
              type="date"
              name="invoice_date"
              className="form-input"
              value={
                formData.invoice_date ? formData.invoice_date.slice(0, 10) : ""
              }
              onChange={handleInputChange}
              required
            />
          </div>

          {/* State */}
          <div className="form-group">
            <label className="form-label">State <span style={{ color: "#ef4444" }}>*</span></label>
            <select
              name="state"
              className="form-input"
              value={formData.state}
              onChange={handleInputChange}
              required
            >
              <option value="">Select State</option>
              {statelist?.map((state) => (
                <option key={state._id} value={state.state_name}>
                  {state.state_name}
                </option>
              ))}
            </select>
          </div>

          {/* District */}
          <div className="form-group">
            <label className="form-label">District (Optional)</label>
            <select
              name="district"
              className="form-input"
              value={formData.district}
              onChange={handleInputChange}
            >
              <option value="">Select District</option>
              {getDistrictsForState().map((district) => (
                <option key={district} value={district}>
                  {district}
                </option>
              ))}
            </select>
          </div>

          {/* VC Name */}
          <div className="form-group">
            <label className="form-label">VC Name <span style={{ color: "#ef4444" }}>*</span></label>
            <select
              name="vc_name"
              className="form-input"
              value={formData.vc_name}
              onChange={handleInputChange}
              disabled={!formData.state}
              required
            >
              <option value="">Select VC Name</option>
              {getVCsForStateAndDistrict().map((vc) => (
                <option key={vc._id} value={vc.name}>
                  {vc.name}
                </option>
              ))}
            </select>
          </div>

          {/* Invoice No */}
          <div className="form-group">
            <label className="form-label">Invoice No. <span style={{ color: "#ef4444" }}>*</span></label>
            <input
              type="text"
              name="invoice_no"
              className="form-input"
              value={formData.invoice_no}
              onChange={handleInputChange}
              placeholder="e.g. INV/2026/001"
              required
            />
          </div>

          {/* Invoice Amount */}
          <div className="form-group">
            <label className="form-label">Invoice Amount (₹) <span style={{ color: "#ef4444" }}>*</span></label>
            <input
              type="number"
              name="invoice_amount"
              min={0}
              className="form-input"
              value={formData.invoice_amount}
              onChange={handleInputChange}
              placeholder="Enter amount in ₹"
              required
            />
          </div>

          {/* Courier Company */}
          <div className="form-group">
            <label className="form-label">Courier Company</label>
            <input
              type="text"
              name="courier_company"
              className="form-input"
              value={formData.courier_company}
              onChange={handleInputChange}
              placeholder="e.g. DTDC, Blue Dart"
            />
          </div>

          {/* Consignment No */}
          <div className="form-group">
            <label className="form-label">Consignment No.</label>
            <input
              type="text"
              name="consignment_no"
              className="form-input"
              value={formData.consignment_no}
              onChange={handleInputChange}
              placeholder="Enter consignment/tracking number"
            />
          </div>

          {/* Bill Type */}
          <div className="form-group">
            <label className="form-label">Bill Type</label>
            <select
              name="bill_type"
              className="form-input"
              value={formData.bill_type}
              onChange={handleInputChange}
            >
              <option value="Regular">Regular</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Bill Month */}
          <div className="form-group">
            <label className="form-label">Bill Period (Month)</label>
            <select
              name="bill_month"
              className="form-input"
              value={formData.bill_month}
              onChange={handleInputChange}
            >
              <option value="">Select Month</option>
              {[
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December",
              ].map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
          </div>

          {/* Bill Year */}
          <div className="form-group">
            <label className="form-label">Bill Period (Year)</label>
            <select
              name="bill_year"
              className="form-input"
              value={formData.bill_year}
              onChange={handleInputChange}
            >
              <option value="">Select Year</option>
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Invoice File */}
          <div className="form-group">
            <label className="form-label">Invoice Attachment</label>
            <div style={{
              border: "1px dashed #cbd5e0",
              borderRadius: "8px",
              padding: "8px 12px",
              backgroundColor: "#f8fafc",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              height: "42px",
              boxSizing: "border-box",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#4a90d9";
              e.currentTarget.style.backgroundColor = "#eff6ff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#cbd5e0";
              e.currentTarget.style.backgroundColor = "#f8fafc";
            }}
            onClick={() => document.getElementById('manualInvoiceFileInput').click()}
            >
              <span style={{ fontSize: "18px" }}>📁</span>
              <span style={{ fontSize: "13px", color: formData.invoiceFile ? "#1a2332" : "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                {formData.invoiceFile ? formData.invoiceFile.name : "Upload invoice PDF/image..."}
              </span>
              {formData.invoiceFile && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFormData(prev => ({ ...prev, invoiceFile: null }));
                    document.getElementById('manualInvoiceFileInput').value = '';
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#ef4444",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                    padding: "0 4px",
                  }}
                >
                  ✕
                </button>
              )}
              <input
                id="manualInvoiceFileInput"
                type="file"
                name="invoiceFile"
                onChange={handleInputChange}
                style={{ display: "none" }}
                accept=".pdf,.doc,.docx,.jpg,.png"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div style={{ gridColumn: isMobile ? "span 1" : "span 3", display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
            <button
              type="submit"
              className="submit-btn"
              style={{
                width: isMobile ? "100%" : "auto",
                minWidth: "220px",
              }}
            >
              <span>💾</span> Submit Invoice Details
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default UploadInvoiceAccordion;
